import { Head, router } from "@inertiajs/react"
import { useState } from "react"
import AdminLayout from "@/components/admin/layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type { Plan } from "@/types"

interface PlanForm {
  stripe_price_id: string
  name: string
  kind: "one_off" | "subscription"
  credits: string
  active: boolean
  position: string
}

const EMPTY_FORM: PlanForm = {
  stripe_price_id: "",
  name:            "",
  kind:            "one_off",
  credits:         "",
  active:          true,
  position:        "0",
}

const KIND_LABELS: Record<string, string> = {
  one_off:      "One-off",
  subscription: "Subscription",
}

interface Props {
  plans: Plan[]
}

export default function AdminPlansIndex({ plans }: Props) {
  const [editing, setEditing] = useState<Plan | null>(null)
  const [editForm, setEditForm] = useState<PlanForm>(EMPTY_FORM)
  const [creating, setCreating] = useState(false)
  const [createForm, setCreateForm] = useState<PlanForm>(EMPTY_FORM)

  function openEdit(plan: Plan) {
    setEditing(plan)
    setEditForm({
      stripe_price_id: plan.stripe_price_id,
      name:            plan.name,
      kind:            plan.kind,
      credits:         String(plan.credits),
      active:          plan.active,
      position:        String(plan.position),
    })
  }

  function handleUpdate() {
    if (!editing) return
    router.patch(`/admin/plans/${editing.id}`, {
      plan: {
        stripe_price_id: editForm.stripe_price_id,
        name:            editForm.name,
        kind:            editForm.kind,
        credits:         Number(editForm.credits),
        active:          editForm.active,
        position:        Number(editForm.position),
      },
    }, {
      onSuccess: () => setEditing(null),
    })
  }

  function openCreate() {
    setCreateForm(EMPTY_FORM)
    setCreating(true)
  }

  function handleCreate() {
    router.post("/admin/plans", {
      plan: {
        stripe_price_id: createForm.stripe_price_id,
        name:            createForm.name,
        kind:            createForm.kind,
        credits:         Number(createForm.credits),
        active:          createForm.active,
        position:        Number(createForm.position),
      },
    }, {
      onSuccess: () => setCreating(false),
    })
  }

  return (
    <AdminLayout>
      <Head title="Plans | Admin" />
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-display text-2xl font-semibold text-cream">Plans</h1>
            <p className="mt-1 text-sm text-cream/50">Stripe billing plans and credit allocations</p>
          </div>
          <Button onClick={openCreate}>New plan</Button>
        </div>

        <div className="overflow-x-auto rounded-lg border border-white/[0.06]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.06] bg-white/[0.02] text-left">
                <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-cream/40">Name</th>
                <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-cream/40">Kind</th>
                <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-cream/40">Stripe Price ID</th>
                <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-cream/40">Credits</th>
                <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-cream/40">Active</th>
                <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-cream/40">Position</th>
                <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-cream/40" />
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <tr key={plan.id} className="border-b border-white/[0.04] text-cream/70">
                  <td className="px-4 py-3 font-medium text-cream">{plan.name}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${
                      plan.kind === "subscription"
                        ? "border border-blue-500/30 bg-blue-500/10 text-blue-300"
                        : "border border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                    }`}>
                      {KIND_LABELS[plan.kind] ?? plan.kind}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-cream/40">{plan.stripe_price_id}</td>
                  <td className="px-4 py-3 tabular-nums">{plan.credits.toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${
                      plan.active
                        ? "border border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                        : "border border-white/10 bg-white/5 text-cream/40"
                    }`}>
                      {plan.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 tabular-nums text-cream/40">{plan.position}</td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-cream/50 hover:text-cream"
                      onClick={() => openEdit(plan)}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
              {plans.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-cream/30">
                    No plans yet. Click "New plan" to add one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="border-white/10 bg-charcoal text-cream">
          <DialogHeader>
            <DialogTitle>Edit plan</DialogTitle>
            <DialogDescription className="text-cream/50">
              ID {editing?.id}
            </DialogDescription>
          </DialogHeader>
          <PlanFormFields form={editForm} onChange={setEditForm} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)} className="text-cream/50">
              Cancel
            </Button>
            <Button onClick={handleUpdate}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create dialog */}
      <Dialog open={creating} onOpenChange={(open) => !open && setCreating(false)}>
        <DialogContent className="border-white/10 bg-charcoal text-cream">
          <DialogHeader>
            <DialogTitle>New plan</DialogTitle>
            <DialogDescription className="text-cream/50">
              Add a Stripe plan and set its credit allocation.
            </DialogDescription>
          </DialogHeader>
          <PlanFormFields form={createForm} onChange={setCreateForm} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreating(false)} className="text-cream/50">
              Cancel
            </Button>
            <Button onClick={handleCreate}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  )
}

function PlanFormFields({
  form,
  onChange,
}: {
  form: PlanForm
  onChange: React.Dispatch<React.SetStateAction<PlanForm>>
}) {
  return (
    <div className="space-y-4 py-2">
      <div>
        <Label className="text-cream/70">Stripe Price ID</Label>
        <Input
          value={form.stripe_price_id}
          onChange={(e) => onChange((f) => ({ ...f, stripe_price_id: e.target.value }))}
          placeholder="price_..."
          className="mt-1 border-white/10 bg-white/5 font-mono text-sm text-cream placeholder:text-cream/20"
        />
      </div>
      <div>
        <Label className="text-cream/70">Name</Label>
        <Input
          value={form.name}
          onChange={(e) => onChange((f) => ({ ...f, name: e.target.value }))}
          placeholder="Starter Pack"
          className="mt-1 border-white/10 bg-white/5 text-cream placeholder:text-cream/20"
        />
      </div>
      <div>
        <Label className="text-cream/70">Kind</Label>
        <Select
          value={form.kind}
          onValueChange={(v) => onChange((f) => ({ ...f, kind: v as PlanForm["kind"] }))}
        >
          <SelectTrigger className="mt-1 border-white/10 bg-white/5 text-cream">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="one_off">One-off</SelectItem>
            <SelectItem value="subscription">Subscription</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-cream/70">Credits</Label>
        <Input
          type="number"
          value={form.credits}
          onChange={(e) => onChange((f) => ({ ...f, credits: e.target.value }))}
          placeholder="20000"
          className="mt-1 border-white/10 bg-white/5 text-cream placeholder:text-cream/20"
        />
      </div>
      <div>
        <Label className="text-cream/70">Position</Label>
        <Input
          type="number"
          value={form.position}
          onChange={(e) => onChange((f) => ({ ...f, position: e.target.value }))}
          className="mt-1 border-white/10 bg-white/5 text-cream"
        />
      </div>
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="plan-active"
          checked={form.active}
          onChange={(e) => onChange((f) => ({ ...f, active: e.target.checked }))}
          className="h-4 w-4 rounded"
        />
        <Label htmlFor="plan-active" className="cursor-pointer text-cream/70">Active</Label>
      </div>
    </div>
  )
}
